import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2, ChevronLeft, ChevronRight, CreditCard, FileDown, Inbox as InboxIcon, Mail, MessageSquare, Phone,
  RefreshCw, User, Video,
} from "lucide-react";
import { Segmented } from "components/ios/Effects";
import { hasCapability, useAdmin } from "components/admin/RequireAdmin";
import { adminRequest, operationKey, queryString } from "lib/adminSiteApi";
import { ErrorNotice, Record, useRemote } from "./Controls";
import { AnnotationForm } from "./Actions";
import { SyncLine } from "./Sync";
import { age } from "./Today";
import { countText, sectionCounts, useSharedToday } from "./Counts";

// The admin route and the API path are the same string.
const BASE = "/admin/submissions";
const PAGE = 50;
const NARROW = "(max-width: 1023.98px)";
const CLOSED = ["closed", "archived"];
const STATUS_WORDS = { new: "New", reviewing: "Reviewing", contacted: "Contacted", qualified: "Qualified", closed: "Closed", archived: "Archived" };
const TONES = { new: "needs", reviewing: "progress", contacted: "progress", qualified: "ok", closed: "closed", archived: "closed" };
const STEPS = ["new", "reviewing", "contacted", "qualified", "closed"].map((value) => ({ value, label: STATUS_WORDS[value] }));
const STATUSES = [{ value: "", label: "Any status" }, ...Object.entries(STATUS_WORDS).map(([value, label]) => ({ value, label }))];
const KINDS = {
  contact: { label: "Contact", icon: MessageSquare, tint: "blue" },
  company: { label: "Company review", icon: Building2, tint: "gold" },
  resource: { label: "Resource", icon: FileDown, tint: "green" },
  webinar: { label: "Webinar", icon: Video, tint: "purple" },
};
const kindOf = (kind, purpose) => kind === "company" && purpose === "paperwork-finder"
  ? { ...KINDS.company, label: "Paperwork finder" }
  : KINDS[kind] || { label: "Enquiry", icon: InboxIcon, tint: "gray" };
const TYPES = [{ value: "", label: "All" }, ...Object.entries(KINDS).map(([value, k]) => ({ value, label: k.label }))];
// Quick views use the list API's own filters where it has them. The API has no "in" or "not
// equal" filter and ignores empty values, so "Unassigned" and "All open" read pages without a
// status/assignee filter and narrow each page here (an open view excludes closed and archived).
// Such a page can show fewer rows than it read; Next keeps paging through the rest. "All" lists
// every lead, closed and archived included, with an optional status filter.
// The list API pages in record-ID order, so rows are newest first only within each page; the
// Inbox opens on "New" (the smallest view) and says so under the list.
const VIEWS = [
  { value: "new", label: "New", params: () => ({ status: "new" }) },
  { value: "mine", label: "Mine", params: (me) => ({ assignedTo: me }) },
  { value: "unassigned", label: "Unassigned", params: () => ({}), keep: (r) => !r.assignedTo },
  { value: "open", label: "All open", params: () => ({}), keep: (r) => !CLOSED.includes(r.status) },
  { value: "all", label: "All", params: (me, status) => ({ status }) },
];
// The answers that matter most for each kind, in reading order; the full form stays folded below.
const ANSWERS = {
  contact: [["subject", "Subject"], ["message", "Message"]],
  company: [["fleetSize", "Fleet size"], ["urgency", "Urgency"], ["painPoints", "Pain points"], ["currentSystems", "Current systems"],
    ["mainProblem", "Main problem"], ["equipmentType", "Equipment"], ["interest", "Interested in"], ["bestTime", "Best time to call"]],
  resource: [["resourceSlug", "Resource"], ["serviceSlug", "Service"], ["fleetSize", "Fleet size"], ["operationType", "Operation"], ["message", "Message"]],
  webinar: [["webinarId", "Webinar"], ["role", "Role"], ["timezone", "Time zone"]],
};

const form = (r) => r?.payload || {};
export const leadName = (r) => {
  const p = form(r);
  return p.name || p.contactName || [p.firstName, p.lastName].filter(Boolean).join(" ") || r?.name || "Unnamed";
};
const leadCompany = (r) => form(r).companyName || form(r).company || "";
const flat = (v) => (Array.isArray(v) ? v.join(", ") : String(v ?? "")).replace(/\s+/g, " ").trim();
function preview(r) {
  const p = form(r);
  const text = r.kind === "contact" ? p.subject || p.message
    : r.kind === "company" ? [p.fleetSize && `Fleet ${p.fleetSize}`, p.equipmentType, p.mainProblem || p.painPoints].filter(Boolean).join(" · ")
      : r.kind === "resource" ? p.resourceSlug || p.serviceSlug || "Resource download"
        : r.kind === "webinar" ? p.webinarId : p.message;
  return flat(text).slice(0, 140);
}
const finderChoice = (value) => ({ w2: "W-2 employees", "owner-operator": "Owner-operators", both: "Both arrangements",
  "box-truck": "Box Truck", "semi-dry-van": "Semi – Dry Van", "semi-reefer": "Semi Reefer", other: "Other", "not-sure": "Not sure yet" })[value] || value;
const answers = (r) => {
  const p = form(r), finder = p.paperworkFinder;
  if (r.purpose === "paperwork-finder" && finder) return [
    ["Working arrangement", finderChoice(finder.workingArrangement)],
    ["Equipment", finderChoice(finder.equipmentType)],
    ["State edition", finderChoice(finder.stateEdition)],
    ["Existing paperwork", finder.existingPaperwork],
    ["Areas needing attention", flat(p.areasNeedAttention)],
    ["Main problem", p.painPoints],
    ["Urgency", p.urgency],
  ].filter(([, value]) => value);
  return (ANSWERS[r.kind] || [["message", "Message"]])
    .map(([key, label]) => [label, flat(p[key])])
    .filter(([, value]) => value)
    .slice(0, 7);
};
const received = (at) => (Number.isFinite(at)
  ? new Date(at).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
  : "");
const narrow = () => Boolean(typeof window !== "undefined" && window.matchMedia?.(NARROW).matches);
const typing = (t) => /^(INPUT|TEXTAREA|SELECT)$/.test(t?.tagName) || t?.isContentEditable;
const DIALOG = '[aria-modal="true"], [role="dialog"], [role="alertdialog"]';
// A status or assignee read in the detail wins over the list row only while it is at least as new.
const merge = (r, saved) => (saved && (saved.revision ?? 0) >= (r.revision ?? 0) ? { ...r, status: saved.status, assignedTo: saved.assignedTo } : r);

export default function Inbox({ id }) {
  const profile = useAdmin(), nav = useNavigate();
  const me = profile?.uid || profile?.id || "";
  const canManage = hasCapability(profile, "crm.manage"), canExport = hasCapability(profile, "crm.export");
  const [view, setView] = useState("new"), [kind, setKind] = useState(""), [status, setStatus] = useState("");
  const [cursor, setCursor] = useState(null), [pages, setPages] = useState([]), [tick, setTick] = useState(0);
  // Status and assignee saved (or reloaded) in the detail, with their revision; cleared on every new read.
  const [fresh, setFresh] = useState({});
  const spec = VIEWS.find((v) => v.value === view);
  const state = useRemote(`${BASE}?${queryString({ ...spec.params(me, status), kind, after: cursor, limit: PAGE })}`, tick);
  const staff = useRemote(canManage ? "/admin/assignees/submissions" : null);
  const names = useMemo(() => Object.fromEntries((staff.data?.items || []).map((s) => [s.id, s.name])), [staff.data]);
  const feed = useSharedToday();
  const newCount = sectionCounts(feed?.data).leads;
  const newText = newCount?.count ? countText(newCount) : null;
  const views = useMemo(() => VIEWS.map((v) => ({ value: v.value, label: v.label, count: v.value === "new" ? newText : null })), [newText]);
  const now = Date.now();
  const rows = (state.data?.items || [])
    .filter((r) => !spec.keep || spec.keep(r))
    .map((r) => merge(r, fresh[r.id]))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  const at = rows.findIndex((r) => r.id === id);
  const list = useRef(null), latest = useRef({ rows, id }), picked = useRef(-1);
  latest.current = { rows, id };
  const open = (rowId) => nav(`${BASE}/${encodeURIComponent(rowId)}`);
  const restart = () => { setCursor(null); setPages([]); setFresh({}); picked.current = -1; };
  const reload = () => { setFresh({}); setTick((n) => n + 1); };
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented || typing(e.target)) return;
      // Never move behind an open dialog such as the search palette.
      if (e.target?.closest?.(DIALOG) || document.querySelector('[aria-modal="true"]')) return;
      const { rows: shown, id: current } = latest.current;
      if (!shown.length || !["j", "k", "Enter"].includes(e.key)) return;
      const links = [...(list.current?.querySelectorAll("a.aw-inbox-row") || [])];
      const focused = links.indexOf(document.activeElement);
      const selected = shown.findIndex((r) => r.id === current);
      if (e.key === "Enter") {
        // Opens the row reached with j/k (it has focus); Enter anywhere else keeps its own meaning.
        if (focused < 0 || !shown[focused] || shown[focused].id === current) return;
        e.preventDefault();
        open(shown[focused].id);
        return;
      }
      e.preventDefault();
      const step = e.key === "j" ? 1 : -1;
      const from = focused >= 0 ? focused : picked.current >= 0 ? picked.current : selected;
      const next = from < 0 ? 0 : Math.min(shown.length - 1, Math.max(0, from + step));
      picked.current = next;
      // With the list hidden (a lead open full screen on a phone), j/k step through the leads instead.
      if (narrow() && current) { if (next !== selected) open(shown[next].id); return; }
      links[next]?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const lastId = useRef(id);
  useEffect(() => {
    // j/k continue from the lead now open, however it was chosen (click, Enter, Previous/Next).
    picked.current = id ? latest.current.rows.findIndex((r) => r.id === id) : -1;
    // Back from a full-screen lead on a phone: return focus to the row it was opened from.
    const was = lastId.current;
    lastId.current = id;
    if (!id && was && narrow()) list.current?.querySelector(`a[href="${BASE}/${encodeURIComponent(was)}"]`)?.focus();
  }, [id]);
  const record = (lead) => setFresh((f) => ({ ...f, [lead.id]: { status: lead.status, assignedTo: lead.assignedTo || "", revision: lead.revision ?? 0 } }));
  return (
    <div className="aw-inbox" data-open={id ? "true" : "false"}>
      <div className="aw-inbox-controls">
        <Segmented label="Quick views" items={views} value={view} onChange={(v) => { setView(v); setStatus(""); restart(); }} className="aw-inbox-views" />
        {view === "all" && (
          <label className="aw-inbox-status">
            <span className="aw-sr">Status</span>
            <select value={status} onChange={(e) => { setStatus(e.target.value); restart(); }}>
              {STATUSES.map((s) => <option key={s.value || "any"} value={s.value}>{s.label}</option>)}
            </select>
          </label>
        )}
        <div className="aw-chips" role="group" aria-label="Type">
          {TYPES.map((t) => (
            <button key={t.value || "all"} type="button" className="ios-press" aria-pressed={kind === t.value}
              onClick={() => { setKind(t.value); restart(); }}>{t.value ? t.label : "All types"}</button>
          ))}
        </div>
        <button type="button" className="ios-btn ios-btn-plain aw-inbox-refresh" aria-label="Refresh leads" onClick={reload}>
          <RefreshCw size={15} aria-hidden="true" /> <span className="aw-inbox-refresh-word">Refresh</span>
        </button>
      </div>
      <div className="aw-inbox-body">
        <section className="aw-inbox-list" aria-label="Leads">
          {state.loading ? (
            <p className="ios-group-footer" role="status">Loading leads…</p>
          ) : state.error ? (
            <ErrorNotice error={state.error} />
          ) : rows.length ? (
            <ul className="ios-group aw-inbox-rows" ref={list}>
              {rows.map((r) => {
                const k = kindOf(r.kind, r.purpose);
                return (
                  <li key={r.id}>
                    <Link className="ios-row aw-inbox-row" to={`${BASE}/${encodeURIComponent(r.id)}`} aria-current={r.id === id ? "true" : undefined}>
                      <span className="ios-tile" data-tint={k.tint}><k.icon aria-hidden="true" /></span>
                      <span className="ios-row-main">
                        <span className="aw-inbox-line">
                          <span className="ios-row-title">{leadName(r)}</span>
                          <span className="aw-inbox-type">{k.label}</span>
                        </span>
                        <span className="ios-row-sub">{[leadCompany(r), preview(r)].filter(Boolean).join(" · ") || "No message"}</span>
                      </span>
                      <span className="aw-inbox-side">
                        <span className="ios-row-meta ios-number">{Number.isFinite(r.createdAt) ? age(r.createdAt, now) : ""}</span>
                        <span className="ios-capsule" data-tone={TONES[r.status] || "closed"}>{STATUS_WORDS[r.status] || r.status || "Not set"}</span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="ios-group aw-inbox-empty">
              <div className="ios-row">
                <span className="ios-tile" data-tint="green"><InboxIcon aria-hidden="true" /></span>
                <span className="ios-row-main">
                  <span className="ios-row-title">{state.data?.next ? "Nothing in this view on this page" : "All clear"}</span>
                  <span className="ios-row-sub">{state.data?.next ? "Use Next to look further." : "No leads match this view."}</span>
                </span>
              </div>
            </div>
          )}
          <div className="aw-inbox-paging">
            {/* aria-disabled, not disabled, so focus stays on the button while the next page loads. */}
            <button type="button" className="ios-btn ios-btn-gray" aria-label="Previous page" aria-disabled={state.loading || !pages.length}
              onClick={() => { if (state.loading || !pages.length) return; setCursor(pages[pages.length - 1]); setPages(pages.slice(0, -1)); picked.current = -1; }}>Previous</button>
            <span className="ios-footnote">Page {pages.length + 1}</span>
            <button type="button" className="ios-btn ios-btn-gray" aria-label="Next page" aria-disabled={state.loading || !!state.error || !state.data?.next}
              onClick={() => { if (state.loading || state.error || !state.data?.next) return; setPages([...pages, cursor]); setCursor(state.data.next); picked.current = -1; }}>Next</button>
          </div>
          <div className="aw-inbox-notes">
            <p className="aw-inbox-order">Sorted newest first within each page of {PAGE}. Pages are not in date order, so a new lead can be on any page.</p>
            <p className="aw-inbox-keys">
              <kbd>j</kbd> <kbd>k</kbd> move · <kbd>Enter</kbd> open
              {canExport && <> · <Link className="aw-link" to="/admin/exports/new?domain=submissions">Export leads</Link></>}
            </p>
          </div>
        </section>
        <section className="aw-inbox-detail" aria-label="Selected lead">
          {id ? (
            <LeadDetail key={id} id={id} me={me} names={names} onFresh={record}
              position={at >= 0 ? { at: at + 1, total: rows.length } : null}
              prev={at > 0 ? rows[at - 1].id : null} next={at >= 0 && at < rows.length - 1 ? rows[at + 1].id : null} />
          ) : (
            <div className="ios-card aw-lead aw-lead-empty">
              <span className="ios-tile" data-tint="blue"><InboxIcon aria-hidden="true" /></span>
              <p className="ios-headline">Choose a lead</p>
              <p className="ios-footnote">Pick a lead on the left to read it here. Press j or k to move and Enter to open.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function LeadDetail({ id, me, names, position, prev, next, onFresh }) {
  const profile = useAdmin(), nav = useNavigate();
  const canManage = hasCapability(profile, "crm.manage");
  const [tick, setTick] = useState(0), [notesTick, setNotesTick] = useState(0);
  const state = useRemote(`${BASE}/${encodeURIComponent(id)}`, tick);
  const notes = useRemote(`${BASE}/${encodeURIComponent(id)}/notes?limit=25`, notesTick);
  const [lead, setLead] = useState(null), [busy, setBusy] = useState(""), [error, setError] = useState(null);
  // stale: "" | "loading" (someone else saved first; re-reading) | "loaded" (the latest version is shown).
  const [notice, setNotice] = useState(""), [stale, setStale] = useState(""), [note, setNote] = useState(""), [more, setMore] = useState(false);
  const pending = useRef(null), heading = useRef(null), focused = useRef(false), report = useRef(onFresh);
  const assignBox = useRef(null), refocus = useRef(false);
  report.current = onFresh;
  useEffect(() => {
    if (!state.data) return;
    setLead(state.data);
    setStale((s) => (s === "loading" ? "loaded" : s));
    report.current?.(state.data);
  }, [state.data]);
  useEffect(() => {
    // On a phone the lead replaces the list, so move focus to its name like a new page. On a wide
    // screen focus stays in the list, unless it was lost with the previous lead (Previous/Next).
    if (!lead || focused.current) return;
    focused.current = true;
    const lost = !document.activeElement || document.activeElement === document.body;
    if (narrow()) window.scrollTo?.(0, 0);
    if (narrow() || lost) heading.current?.focus({ preventScroll: true });
  }, [lead]);
  useEffect(() => {
    // Assign to me and Unassign replace each other: keep focus on the button that is left.
    if (!refocus.current || busy) return;
    refocus.current = false;
    if (!document.activeElement || document.activeElement === document.body) assignBox.current?.querySelector("button")?.focus();
  });
  const path = `${BASE}/${encodeURIComponent(id)}`;
  // Controls stay focusable while a save or a re-read runs (aria-disabled); the guard here ignores them.
  const blocked = Boolean(busy) || state.loading || stale === "loading";
  // Same PATCH and fields as the "Review and assign" form; the record revision guards every save.
  const save = async (fields, what) => {
    if (blocked || !lead) return;
    const body = { revision: lead.revision, ...fields }, digest = JSON.stringify(body);
    if (pending.current?.digest !== digest) pending.current = { digest, key: operationKey() };
    setBusy(what); setError(null); setNotice(""); setStale("");
    try {
      const result = await adminRequest(path, { method: "PATCH", body, key: pending.current.key });
      pending.current = null;
      const saved = { ...lead, revision: result?.data?.revision ?? lead.revision + 1 };
      if (fields.status !== undefined) saved.status = fields.status;
      if (fields.assignedTo !== undefined) saved.assignedTo = fields.assignedTo;
      setLead(saved);
      onFresh(saved);
      if (what === "note") { setNote(""); setNotesTick((n) => n + 1); }
      if (what === "assign") refocus.current = true;
      setNotice(what === "note" ? "Note saved." : what === "status" ? `Status set to ${STATUS_WORDS[fields.status]}.` : fields.assignedTo ? "Assigned to you." : "Unassigned.");
    } catch (e) {
      if (e?.code === "stale_revision" || e?.status === 409) {
        pending.current = null;
        setStale("loading");
        setTick((n) => n + 1);
      } else setError(e);
    } finally {
      setBusy("");
    }
  };
  const reload = () => { setStale((s) => (s ? "loading" : "")); setTick((n) => n + 1); };
  if (!lead)
    return (
      <div className="ios-card aw-lead">
        <BackLink />
        {state.error ? <ErrorNotice error={state.error} /> : <p className="ios-footnote" role="status">Loading lead…</p>}
      </div>
    );
  const p = form(lead), k = kindOf(lead.kind, lead.purpose), name = leadName(lead), company = leadCompany(lead);
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("") || "?";
  const dot = p.dotNumber || "", mc = p.mcNumber || "", email = p.email || lead.email || "", phone = p.phone || "";
  const told = answers(lead);
  const assignee = lead.assignedTo ? (lead.assignedTo === me ? "you" : names[lead.assignedTo] || "another team member") : "nobody yet";
  const links = [
    lead.contactId && { to: `/admin/contacts/${encodeURIComponent(lead.contactId)}`, icon: User, tint: "blue", title: "Contact", sub: name },
    lead.companyId && { to: `/admin/companies/${encodeURIComponent(lead.companyId)}`, icon: Building2, tint: "orange", title: "Company", sub: company || "Company record" },
    lead.orderId && hasCapability(profile, "commerce.read") && { to: `/admin/orders/${encodeURIComponent(lead.orderId)}`, icon: CreditCard, tint: "green", title: "Order", sub: `MAG-${String(lead.orderId).slice(0, 8).toUpperCase()}` },
  ].filter(Boolean);
  const staffNotes = [...(notes.data?.items || [])].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  const go = (target) => nav(`${BASE}/${encodeURIComponent(target)}`);
  return (
    <article className="ios-card aw-lead" aria-labelledby={`aw-lead-${id}`}>
      <header className="aw-lead-head">
        <BackLink />
        <span className="aw-lead-avatar" aria-hidden="true">{initials}</span>
        <div className="aw-lead-id">
          <p className="ios-eyebrow">{[k.label, lead.createdAt ? `Received ${received(lead.createdAt)}` : ""].filter(Boolean).join(" · ")}</p>
          <h2 id={`aw-lead-${id}`} ref={heading} tabIndex={-1}>{name}</h2>
          {(company || dot || mc || p.dotMc) && (
            <p className="aw-lead-org">{[company, dot && `DOT ${dot}`, mc && `MC ${mc}`, !dot && !mc && p.dotMc ? `DOT/MC ${p.dotMc}` : ""].filter(Boolean).join(" · ")}</p>
          )}
          {(email || phone) && (
            <p className="aw-lead-reach">
              {email && <a className="aw-link" href={`mailto:${email}`}><Mail size={15} aria-hidden="true" /> {email}</a>}
              {phone && <a className="aw-link" href={`tel:${phone.replace(/[^\d+]/g, "")}`}><Phone size={15} aria-hidden="true" /> {phone}</a>}
            </p>
          )}
          {lead.attribution?.sourcePage && <p className="ios-footnote">From {lead.attribution.sourcePage}</p>}
        </div>
        <div className="aw-lead-nav">
          {position && <span className="ios-caption ios-number">{position.at} of {position.total}</span>}
          <button type="button" className="ios-btn ios-btn-gray ios-icon-btn" aria-label="Previous lead" disabled={!prev} onClick={() => go(prev)}><ChevronLeft size={18} /></button>
          <button type="button" className="ios-btn ios-btn-gray ios-icon-btn" aria-label="Next lead" disabled={!next} onClick={() => go(next)}><ChevronRight size={18} /></button>
          <button type="button" className="ios-btn ios-btn-gray ios-icon-btn" aria-label="Reload this lead" onClick={reload}><RefreshCw size={16} /></button>
        </div>
        <span className="aw-lead-break" aria-hidden="true" />
      </header>
      {stale === "loaded" && (
        <div className="aw-notice aw-error" role="alert">
          <strong>Someone else changed this lead</strong>
          <p>The latest version is loaded now. Check it and choose again.{note.trim() ? " Your note is still in the box." : ""}</p>
        </div>
      )}
      {stale === "loading" && (
        <div className="aw-notice aw-error" role={state.error ? "alert" : "status"}>
          <strong>Someone else changed this lead</strong>
          <p>{state.error ? "The latest version could not be loaded. Use Reload this lead before choosing again." : "Loading the latest version…"}</p>
        </div>
      )}
      <ErrorNotice error={state.error} />
      <ErrorNotice error={error} />
      <div className="aw-lead-state">
        {canManage ? (
          <fieldset className="aw-lead-fieldset" aria-busy={blocked}>
            <legend className="aw-sr">Status</legend>
            <Segmented label="Lead status" items={STEPS} value={lead.status} className="aw-lead-status" busy={blocked}
              onChange={(value) => { if (value !== lead.status) save({ status: value }, "status"); }} />
          </fieldset>
        ) : (
          <span className="ios-capsule" data-tone={TONES[lead.status] || "closed"}>{STATUS_WORDS[lead.status] || lead.status}</span>
        )}
        {canManage && lead.status === "archived" && <span className="ios-capsule" data-tone="closed">Archived</span>}
      </div>
      <div className="aw-lead-assign" ref={assignBox}>
        <span className="ios-footnote">Assigned to <strong>{assignee}</strong></span>
        {canManage && lead.assignedTo !== me && (
          <button type="button" className="ios-btn ios-btn-tinted" aria-disabled={blocked} onClick={() => save({ assignedTo: me }, "assign")}>Assign to me</button>
        )}
        {canManage && lead.assignedTo && (
          <button type="button" className="ios-btn ios-btn-gray" aria-disabled={blocked} onClick={() => save({ assignedTo: "" }, "assign")}>Unassign</button>
        )}
      </div>
      {notice && <p className="aw-lead-saved" role="status">{notice}</p>}
      {told.length > 0 && (
        <>
          <h3 className="ios-group-header"><span>What they told us</span></h3>
          <dl className="ios-group aw-lead-answers">
            {told.map(([label, value]) => (
              <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
            ))}
          </dl>
        </>
      )}
      {links.length > 0 && (
        <>
          <h3 className="ios-group-header"><span>Linked</span></h3>
          <ul className="ios-group">
            {links.map((l) => (
              <li key={l.to}>
                <Link className="ios-row" to={l.to}>
                  <span className="ios-tile" data-tint={l.tint}><l.icon aria-hidden="true" /></span>
                  <span className="ios-row-main"><span className="ios-row-title">{l.title}</span><span className="ios-row-sub">{l.sub}</span></span>
                  <ChevronRight size={16} className="ios-chevron" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
      {hasCapability(profile, "integrations.read") && <div className="aw-lead-sync"><SyncLine type="submission" id={id} /></div>}
      {canManage && (
        <form className="aw-lead-note" onSubmit={(e) => { e.preventDefault(); if (note.trim()) save({ note: note.trim() }, "note"); }}>
          <label htmlFor={`aw-note-${id}`}>Note for the team</label>
          <textarea id={`aw-note-${id}`} rows={3} maxLength={10000} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Called, left voicemail. Try again Thursday." />
          <button type="submit" className="ios-btn" aria-disabled={blocked || !note.trim()}>{busy === "note" ? "Saving…" : "Save note"}</button>
        </form>
      )}
      <h3 className="ios-group-header"><span>Team notes</span></h3>
      {notes.error ? <ErrorNotice error={notes.error} /> : staffNotes.length ? (
        <ul className="ios-group aw-lead-notes">
          {staffNotes.map((n) => (
            <li key={n.id}>
              <p>{n.note}</p>
              <span className="ios-caption">{[n.actor === me ? "You" : names[n.actor] || "Team member", received(n.createdAt)].filter(Boolean).join(" · ")}</span>
            </li>
          ))}
        </ul>
      ) : <p className="ios-group-footer">{notes.loading ? "Loading notes…" : "No notes yet."}</p>}
      <details className="aw-lead-fold">
        <summary>All submitted answers</summary>
        <Record data={lead} />
      </details>
      {canManage && (
        <details className="aw-lead-fold" onToggle={(e) => setMore(e.currentTarget.open)}>
          <summary>More options: assign to someone else, tags, archive</summary>
          {more && <AnnotationForm domain="submissions" record={lead} onDone={() => { setTick((n) => n + 1); setNotesTick((n) => n + 1); }} />}
        </details>
      )}
    </article>
  );
}

function BackLink() {
  return (
    <Link className="aw-link aw-inbox-back" to={BASE}>
      <ChevronLeft size={18} aria-hidden="true" /> Inbox
    </Link>
  );
}
